import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import LoadingOverlay from "./LoadingOverlay";
import ClientList from "./ClientList";

const API = "https://eps.elkomprintama.com/";

const today = () => new Date().toISOString().split("T")[0];
const firstOfMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};

const TABS = ["By Date", "By Customer", "By PO Number", "By Part Number"];

const thCls = "px-4 py-3 border-r whitespace-nowrap text-left";
const tdCls = "px-4 py-2 border-r whitespace-nowrap";

function ResultTable({ rows }) {
  if (rows.length === 0)
    return (
      <div className="text-center py-16 text-gray-400 italic text-sm">
        No records found.
      </div>
    );

  return (
    <div className="flex-1 min-h-0 overflow-auto rounded-lg shadow">
      <table className="text-xs w-full border-collapse">
        <thead className="sticky top-0 text-white uppercase bg-purple-700">
          <tr>
            <th className={thCls}>No</th>
            <th className={thCls}>Finish Date</th>
            <th className={thCls}>Customer</th>
            <th className={thCls}>Job Name</th>
            <th className={thCls}>Part No. (Cust)</th>
            <th className={thCls}>Size</th>
            <th className="px-4 py-3 border-r whitespace-nowrap bg-purple-800">
              Finish Qty
            </th>
            <th className={thCls}>PO Number</th>
            <th className={thCls}>Operator</th>
            <th className={thCls}>QC</th>
            <th className={thCls}>Prepared By</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((o, i) => (
            <tr
              key={o.fg_id}
              className={`border-b hover:bg-purple-50 ${i % 2 === 0 ? "bg-gray-50" : "bg-white"}`}
            >
              <td className={`${tdCls} text-gray-500`}>{i + 1}</td>
              <td className={tdCls}>
                {o.finish_date
                  ? new Date(o.finish_date).toLocaleDateString()
                  : "—"}
              </td>
              <td className={`${tdCls} font-medium`}>{o.nme}</td>
              <td className={tdCls}>{o.job_name}</td>
              <td className={tdCls}>{o.pn_cust || "—"}</td>
              <td className={tdCls}>{o.size}</td>
              <td className="px-4 py-2 border-r text-center font-bold bg-purple-100 text-purple-800">
                {o.finish_qty}
              </td>
              <td className={tdCls}>{o.po_num}</td>
              <td className={tdCls}>{o.opr_name}</td>
              <td className={tdCls}>{o.qc_name}</td>
              <td className={tdCls}>{o.prepared_by || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FinishedGoods() {
  const [activeTab, setActiveTab] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // By Date state
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(today());
  const [dateRows, setDateRows] = useState([]);
  const [dateSearched, setDateSearched] = useState(false);

  // By Customer state
  const [custId, setCustId] = useState(null);
  const [custRows, setCustRows] = useState([]);
  const [custSearched, setCustSearched] = useState(false);

  // By PO Number state
  const [poInput, setPoInput] = useState("");
  const [poRows, setPoRows] = useState([]);
  const [poSearched, setPoSearched] = useState(false);

  // By Part Number state
  const [partInput, setPartInput] = useState("");
  const [partRows, setPartRows] = useState([]);
  const [partSearched, setPartSearched] = useState(false);

  const fetchByDate = async () => {
    if (!from || !to) {
      toast.warning("Select both dates.");
      return;
    }
    setIsLoading(true);
    try {
      const res = await axios.get(
        `${API}get/api/fin_orders/?from=${from}&to=${to}`,
        { withCredentials: true },
      );
      setDateRows(res.data.result || []);
      setDateSearched(true);
    } catch {
      toast.error("Failed to fetch records.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchByCustomer = async () => {
    if (!custId) {
      toast.warning("Select a customer first.");
      return;
    }
    setIsLoading(true);
    try {
      const res = await axios.get(
        `${API}get/api/fin_orders/by-customer?cust_id=${custId}`,
        { withCredentials: true },
      );
      setCustRows(res.data.result || []);
      setCustSearched(true);
    } catch {
      toast.error("Failed to fetch records.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchByPO = async () => {
    if (!poInput.trim()) {
      toast.warning("Enter a PO number to search.");
      return;
    }
    setIsLoading(true);
    try {
      const res = await axios.get(
        `${API}get/api/fin_orders/by-po?po_num=${encodeURIComponent(poInput.trim())}`,
        { withCredentials: true },
      );
      setPoRows(res.data.result || []);
      setPoSearched(true);
    } catch {
      toast.error("Failed to fetch records.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchByPart = async () => {
    if (!partInput.trim()) {
      toast.warning("Enter a part number to search.");
      return;
    }
    setIsLoading(true);
    try {
      const res = await axios.get(
        `${API}get/api/fin_orders/by-part?pn_cust=${encodeURIComponent(partInput.trim())}`,
        { withCredentials: true },
      );
      setPartRows(res.data.result || []);
      setPartSearched(true);
    } catch {
      toast.error("Failed to fetch records.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col" style={{ maxHeight: "calc(100vh - 80px)" }}>
      <div className="w-full rounded-lg bg-purple-600 mb-4 flex-shrink-0">
        <label className="block uppercase text-white tracking-wide text-5xl text-center font-bold py-2">
          Finished Goods
        </label>
      </div>

      <div className="flex-1 min-h-0 flex flex-col bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden relative">
        {isLoading && <LoadingOverlay message="Loading…" />}

        {/* Tab bar */}
        <div className="flex border-b border-gray-200 px-4 pt-3 gap-1 bg-gray-50 flex-shrink-0">
          {TABS.map((tab, i) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(i)}
              className={`px-5 py-2 text-sm font-semibold rounded-t-lg transition focus:outline-none ${
                activeTab === i
                  ? "bg-purple-600 text-white"
                  : "text-gray-500 hover:text-purple-600 hover:bg-purple-50"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="flex-1 min-h-0 flex flex-col p-5 gap-4">
          {/* Tab 1 — By Date */}
          {activeTab === 0 && (
            <>
              <div className="flex-shrink-0 flex flex-wrap items-center gap-2">
                <span className="text-xs text-gray-500 font-semibold">
                  Date range:
                </span>
                <input
                  type="date"
                  className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-purple-400"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                />
                <span className="text-xs text-gray-400">to</span>
                <input
                  type="date"
                  className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-purple-400"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                />
                <button
                  className="bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold px-5 py-1.5 rounded-lg transition"
                  onClick={fetchByDate}
                >
                  Search
                </button>
                {dateSearched && (
                  <span className="text-xs text-gray-400 ml-2">
                    {dateRows.length} record{dateRows.length !== 1 ? "s" : ""}
                  </span>
                )}
              </div>

              {!dateSearched ? (
                <div className="flex-1 flex items-center justify-center text-gray-400 italic text-sm">
                  Select a date range and click Search
                </div>
              ) : (
                <ResultTable rows={dateRows} />
              )}
            </>
          )}

          {/* Tab 2 — By Customer */}
          {activeTab === 1 && (
            <>
              <div className="flex-shrink-0 flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-56">
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    Customer
                  </label>
                  <ClientList
                    onChangeList={setCustId}
                    onInitial={setCustId}
                    showLabel={false}
                  />
                </div>
                <button
                  className="bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold px-5 py-2 rounded-lg transition"
                  onClick={fetchByCustomer}
                >
                  Search
                </button>
                {custSearched && (
                  <span className="text-xs text-gray-400">
                    {custRows.length} record{custRows.length !== 1 ? "s" : ""}
                  </span>
                )}
              </div>

              {!custSearched ? (
                <div className="flex-1 flex items-center justify-center text-gray-400 italic text-sm">
                  Select a customer and click Search
                </div>
              ) : (
                <ResultTable rows={custRows} />
              )}
            </>
          )}

          {/* Tab 3 — By PO Number */}
          {activeTab === 2 && (
            <>
              <div className="flex-shrink-0 flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-56">
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    PO Number
                  </label>
                  <input
                    name="poinput"
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-purple-400"
                    placeholder="e.g. PO-12345"
                    value={poInput}
                    onChange={(e) => setPoInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && fetchByPO()}
                  />
                </div>
                <button
                  className="bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold px-5 py-2 rounded-lg transition"
                  onClick={fetchByPO}
                >
                  Search
                </button>
                {poSearched && (
                  <span className="text-xs text-gray-400">
                    {poRows.length} record{poRows.length !== 1 ? "s" : ""}
                  </span>
                )}
              </div>

              {!poSearched ? (
                <div className="flex-1 flex items-center justify-center text-gray-400 italic text-sm">
                  Enter a PO number and click Search
                </div>
              ) : (
                <ResultTable rows={poRows} />
              )}
            </>
          )}

          {/* Tab 4 — By Part Number */}
          {activeTab === 3 && (
            <>
              <div className="flex-shrink-0 flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-56">
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    Part Number (Cust. P/N)
                  </label>
                  <input
                    name="partinput"
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-purple-400"
                    placeholder="e.g. PN-001"
                    value={partInput}
                    onChange={(e) => setPartInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && fetchByPart()}
                  />
                </div>
                <button
                  className="bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold px-5 py-2 rounded-lg transition"
                  onClick={fetchByPart}
                >
                  Search
                </button>
                {partSearched && (
                  <span className="text-xs text-gray-400">
                    {partRows.length} record{partRows.length !== 1 ? "s" : ""}
                  </span>
                )}
              </div>

              {!partSearched ? (
                <div className="flex-1 flex items-center justify-center text-gray-400 italic text-sm">
                  Enter a part number and click Search
                </div>
              ) : (
                <ResultTable rows={partRows} />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default FinishedGoods;
